import React, { useState } from 'react';
import { Collapse, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useEventTracker } from '../eventtracker/eventTrackerContext';

type ListButtonVerticalBarProps = {
    lists: {
        title: string;
        items: (ItemType & { subItems?: ItemType[]; route?: string })[];
    }[];
    firstSelectedItem: number;
};

type ItemType = {
    icon?: React.ReactElement;
    text: string;
    onClick?: () => void;
};

const ListButtonVerticalBar: React.FC<ListButtonVerticalBarProps> = ({ lists, firstSelectedItem }) => {

    const { trackEvent } = useEventTracker();

    // Open all lists by default.
    const initialOpenLists = lists.reduce((acc, list) => {
        acc[list.title] = true;
        return acc;
    }, {} as { [key: string]: boolean });
    const [openLists, setOpenLists] = useState<{ [key: string]: boolean }>(initialOpenLists);

    const handleToggleList = (title: string) => {
        setOpenLists({ ...openLists, [title]: !openLists[title] });
    };

    // Flatten all items from every list.
    const flattenedItems = lists.reduce((acc, list) => acc.concat(list.items), [] as ItemType[]);
    // Use firstSelectedItem index (or fallback to 0) to determine the initial selected item text.
    const initialSelectedText = flattenedItems.length > firstSelectedItem
        ? flattenedItems[firstSelectedItem].text
        : flattenedItems.length > 0
            ? flattenedItems[0].text
            : null;

    const [selectedItem, setSelectedItem] = useState<string | null>(initialSelectedText);
    const [selectedSubItem, setSelectedSubItem] = useState<string | null>(null);

    const handleItemClick = (itemText: string, itemOnClick?: () => void, isSubItem: boolean = false) => {
        if (isSubItem) {
            setSelectedSubItem(itemText);
            setSelectedItem(null);
        } else {
            trackEvent("verticalbar", { item: itemText });
            setSelectedItem(itemText);
            setSelectedSubItem(null);
        }
        itemOnClick?.();
    };

    const isItemSelectedOrSubItemSelected = (item: any, selectedItem: any, selectedSubItem: any) => {
        if (selectedItem === item.text) {
            return true;
        }
        if (item.subItems) {
            return item.subItems.some((subItem: { text: any; }) => selectedSubItem === subItem.text);
        }
        return false;
    };

    return (
        <List sx={{ width: '100%' }} component="nav" aria-labelledby="nested-list-subheader">
            {
                lists.map(list => (
                    <React.Fragment key={list.title}>
                        <ListItem style={{ margin: "0px 8px" }}>
                            <ListItemText
                                disableTypography
                                primary={list.title}
                                sx={theme => ({
                                    fontSize: "0.75rem",
                                    fontWeight: "700",
                                    lineHeight: "1.5",
                                    textTransform: "uppercase",
                                    display: "inlineFlex",
                                    color: "rgb(145, 158, 171)",
                                    marginBottom: "4px",
                                    transition: "color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms"
                                })}
                            />
                            {openLists[list.title]}
                        </ListItem>
                        <Collapse in={true} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {
                                    list.items.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <ListItemButton
                                                onClick={() => item.subItems ? handleToggleList(item.text) : handleItemClick(item.text, item.onClick)}
                                                sx={theme => ({
                                                    backgroundColor: isItemSelectedOrSubItemSelected(item, selectedItem, selectedSubItem) ? theme.palette.primary.light : undefined,
                                                    border: isItemSelectedOrSubItemSelected(item, selectedItem, selectedSubItem) ? `1px solid #b5bffd` : "none",
                                                    boxSizing: "border-box",
                                                    margin: "2px 8px",
                                                    borderRadius: "8px"
                                                })}
                                            >
                                                <ListItemIcon sx={theme => ({
                                                    minWidth: "40px",
                                                    color: isItemSelectedOrSubItemSelected(item, selectedItem, selectedSubItem) ? theme.palette.primary.main : "#637381",
                                                })}>
                                                    {item.icon}
                                                </ListItemIcon>
                                                <ListItemText primary={
                                                    <Typography sx={theme => ({
                                                        width: "100%",
                                                        maxWidth: "100%",
                                                        display: "block",
                                                        overflow: "hidden",
                                                        whiteSpace: "nowrap",
                                                        textOverflow: "ellipsis",
                                                        color: isItemSelectedOrSubItemSelected(item, selectedItem, selectedSubItem) ? theme.palette.primary.main : "#637381",
                                                        lineHeight: "1.57143",
                                                        fontSize: "0.875rem",
                                                        fontWeight: isItemSelectedOrSubItemSelected(item, selectedItem, selectedSubItem) ? "600" : "500",
                                                        textTransform: "capitalize"
                                                    })}>
                                                        {item.text}
                                                    </Typography>
                                                } />
                                                {
                                                    item.subItems && (
                                                        <Icon
                                                            icon={openLists[item.text] ? "iconamoon:arrow-down-2-duotone" : "iconamoon:arrow-right-2-duotone"}
                                                            color={isItemSelectedOrSubItemSelected(item, selectedItem, selectedSubItem) ? "rgb(32, 101, 209)" : "#637381"}
                                                        />
                                                    )
                                                }
                                            </ListItemButton>
                                            {
                                                item.subItems && (
                                                    <Collapse in={openLists[item.text]} timeout="auto" unmountOnExit>
                                                        <List component="div" disablePadding>
                                                            {
                                                                item.subItems.map((subItem, subIndex) => (
                                                                    <ListItemButton
                                                                        key={`sub-${subIndex}`}
                                                                        onClick={() => handleItemClick(subItem.text, subItem.onClick, true)}
                                                                        sx={theme => ({
                                                                            pl: 4,
                                                                            '&:hover': { backgroundColor: "action.hover" },
                                                                            borderRadius: "8px",
                                                                            margin: "2px 8px"
                                                                        })}
                                                                    >
                                                                        <ListItemIcon>
                                                                            <Icon
                                                                                color={selectedSubItem === subItem.text ? "rgb(32, 101, 209)" : "#637381"}
                                                                                icon={"tabler:point-filled"}
                                                                                width={selectedSubItem === subItem.text ? "20" : "15"}
                                                                            />
                                                                        </ListItemIcon>
                                                                        <ListItemText
                                                                            primary={
                                                                                <Typography style={{ fontSize: "0.875rem" }}
                                                                                    marginLeft="-15px"
                                                                                    fontWeight={selectedSubItem === subItem.text ? "600" : undefined}
                                                                                    color={"#637381"}
                                                                                >
                                                                                    {subItem.text}
                                                                                </Typography>
                                                                            }
                                                                        />
                                                                    </ListItemButton>
                                                                ))
                                                            }
                                                        </List>
                                                    </Collapse>
                                                )
                                            }
                                        </React.Fragment>
                                    ))
                                }
                            </List>
                        </Collapse>
                    </React.Fragment>
                ))
            }
        </List>
    );
};

export default ListButtonVerticalBar;
